import { calculateToaScheduling } from '../utils/toaSchedulingCalculator';
import { getServicePaperIds } from '../utils/toaSchedulingDetails';

const read = (row, ...names) => {
  for (const name of names) {
    if (row && Object.prototype.hasOwnProperty.call(row, name)) return row[name];
  }
  return undefined;
};

const textKey = value => String(value ?? '').trim();
const techCodeOf = row => read(row, 'TechCode', 'TEKNISYEN_KODU', 'techCode');
const userTechCodeOf = row => read(row, 'UserTechCode', 'KUL_TEK_KODU', 'userTechCode');
const techNameOf = row => read(row, 'TechName', 'TEKNISYEN_ADI', 'techName') || '';
const ofscNrOf = row => read(row, 'OfscNr', 'OFSC_NR', 'ofscNr');
const skillIdOf = row => read(row, 'SkillId', 'UZMANLIK_KODU', 'skillId');
const neighbourhoodOf = row => read(row, 'Neighbourhood', 'NEIGHBOURHOOD', 'neighbourhood') || '';
const isPlaceholder = row => textKey(ofscNrOf(row)) === '0';
const isBucketNeighbourhood = row => neighbourhoodOf(row).trim().toLocaleLowerCase('tr-TR') === 'bucket';

const parseNumber = value => {
  const parsed = Number(String(value ?? '').trim().replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

const parseMinutes = value => {
  const match = String(value ?? '').match(/(?:T|\s|^)(\d{1,2}):(\d{2})/);
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
};

const statusKey = row => String(read(row, 'StatusDescription', 'statusDescription') || '')
  .trim()
  .toLocaleLowerCase('tr-TR');

const isMovable = row => !['completed', 'road', 'started'].includes(statusKey(row));
const CLOSED_VIOLATION_STATUSES = new Set([
  'completed',
  'cancelled',
  'notdone',
  'tamamlandı',
  'tamamlandi',
  'iptal',
  'iptal edildi',
  'yapılmadı',
  'yapilmadi',
]);
const isOpenForViolation = row => !CLOSED_VIOLATION_STATUSES.has(statusKey(row));
const isCompleted = row => ['completed', 'tamamlandı', 'tamamlandi'].includes(statusKey(row));

const dealerOf = (row) => {
  const direct = read(
    row,
    'Dealer', 'DealerName', 'Bayi', 'BayiName',
    'dealer', 'dealerName', 'bayi', 'bayiName',
  );
  if (direct && !/^(null|undefined|-)$/i.test(String(direct).trim())) {
    return String(direct).trim();
  }
  const servicePapers = read(row, 'ServicePapers', 'servicePapers', 'FisNoDetay', 'fisNoDetay');
  const rawCustomer = String(servicePapers || '')
    .split(',')
    .map(item => item.trim().split('!')[0].split('|')[1]?.trim() || '')
    .find(Boolean) || '';
  const match = rawCustomer.match(/Bayi:\s*(.+)/i);
  return match
    ? match[1].replace(/\d{7,}/g, '').replace(/[-|]/g, ' ').replace(/\s+/g, ' ').trim()
    : '';
};

const displayWorkDurationOf = (row) => {
  const plannedDuration = Number(read(row, 'WorkDuration', 'Duration', 'workDuration')) || 0;
  if (!isCompleted(row)) return plannedDuration;
  const arrival = parseMinutes(read(row, 'ArrivalatCustomer', 'arrivalatCustomer'));
  const completion = parseMinutes(read(row, 'TaskCompletionTime', 'taskCompletionTime'));
  return arrival != null && completion != null ? Math.max(0, completion - arrival) : 0;
};

const coordinateOf = row => ({
  latitude: parseNumber(read(row, 'LATITUDE', 'Latitude', 'latitude')),
  longitude: parseNumber(read(row, 'LONGITUDE', 'Longitude', 'longitude')),
});

const haversineKm = (from, to) => {
  const toRad = degree => degree * Math.PI / 180;
  const dLat = toRad(to.latitude - from.latitude);
  const dLon = toRad(to.longitude - from.longitude);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(from.latitude)) * Math.cos(toRad(to.latitude)) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const resolveCenter = (rows, options = {}) => {
  const bucketPoint = rows
    .filter(isBucketNeighbourhood)
    .map(coordinateOf)
    .find(point => point.latitude != null && point.longitude != null);
  if (bucketPoint) return bucketPoint;

  const supplied = options.serviceCenter || {};
  const suppliedPoint = {
    latitude: parseNumber(supplied.latitude ?? supplied.Latitude),
    longitude: parseNumber(supplied.longitude ?? supplied.Longitude),
  };
  if (suppliedPoint.latitude && suppliedPoint.longitude) return suppliedPoint;

  const points = rows
    .filter(row => !isPlaceholder(row))
    .map(coordinateOf)
    .filter(point => point.latitude != null && point.longitude != null);
  if (!points.length) return { latitude: 0, longitude: 0 };
  return {
    latitude: points.reduce((sum, point) => sum + point.latitude, 0) / points.length,
    longitude: points.reduce((sum, point) => sum + point.longitude, 0) / points.length,
  };
};

const toJobRow = row => ({
  ofscNr: ofscNrOf(row),
  sortOrder: Number(read(row, 'SortOrder', 'sortOrder')) || 0,
  isPlaceholder: isPlaceholder(row),
  neighbourhood: read(row, 'Neighbourhood', 'neighbourhood') || '',
  customer: read(row, 'Customer', 'CustomerName', 'customer') || '',
  dealer: dealerOf(row),
  latitude: read(row, 'LATITUDE', 'Latitude', 'latitude'),
  longitude: read(row, 'LONGITUDE', 'Longitude', 'longitude'),
  appReason: read(row, 'AppReason', 'ApplicationReason', 'appReason') || '',
  products: read(row, 'Products', 'products') || '',
  servicePapers: read(row, 'ServicePapers', 'servicePapers', 'FisNoDetay', 'fisNoDetay') || '',
  servicePaperCount: getServicePaperIds(
    read(row, 'ServicePapers', 'servicePapers', 'FisNoDetay', 'fisNoDetay'),
  ).length,
  appointmentTime: read(row, 'AppointmentTime', 'appointmentTime') || '',
  appointmentTimeEnd: read(row, 'AppointmentTimeEnd', 'appointmentTimeEnd') || '',
  departureTime: read(row, 'DepartureTime', 'departureTime') || '',
  arrivalatCustomer: read(row, 'ArrivalatCustomer', 'arrivalatCustomer') || '',
  taskCompletionTime: read(row, 'TaskCompletionTime', 'taskCompletionTime') || '',
  workDuration: Number(read(row, 'WorkDuration', 'Duration', 'workDuration')) || 0,
  displayWorkDuration: displayWorkDurationOf(row),
  workDurationType: isCompleted(row) ? 'Gerçekleşen süre' : 'Planlanan süre',
  idleDuration: Number(read(row, 'IdleDuration', 'idleDuration')) || 0,
  travelDuration: Number(read(row, 'TravelDuration', 'travelDuration')) || 0,
  statusDescription: read(row, 'StatusDescription', 'statusDescription') || '',
  isEtaViolation: (() => {
    const arrival = parseMinutes(read(row, 'ArrivalatCustomer', 'arrivalatCustomer'));
    const end = parseMinutes(read(row, 'AppointmentTimeEnd', 'appointmentTimeEnd'));
    return !isPlaceholder(row) && arrival != null && end != null && arrival > end;
  })(),
  isCountedEtaViolation: (() => {
    const arrival = parseMinutes(read(row, 'ArrivalatCustomer', 'arrivalatCustomer'));
    const end = parseMinutes(read(row, 'AppointmentTimeEnd', 'appointmentTimeEnd'));
    return !isPlaceholder(row)
      && isOpenForViolation(row)
      && arrival != null
      && end != null
      && arrival > end;
  })(),
});

const summarizeRoute = (techCode, rows, center, fallbackName = '') => {
  const orderedRows = rows
    .filter(row => textKey(techCodeOf(row)) === textKey(techCode))
    .sort((first, second) => Number(first.SortOrder || 0) - Number(second.SortOrder || 0));
  const points = orderedRows
    .filter(row => !isPlaceholder(row) && !isBucketNeighbourhood(row))
    .map(coordinateOf)
    .filter(point => point.latitude != null && point.longitude != null);
  let totalKm = 0;
  let previous = center;
  points.forEach((point) => {
    totalKm += haversineKm(previous, point) * 1.4;
    previous = point;
  });
  if (points.length) totalKm += haversineKm(previous, center) * 1.4;

  const workEnd = orderedRows.map(row => parseMinutes(row.TechWorkEnd)).find(value => value != null) ?? 18 * 60;
  const lastCompletion = orderedRows
    .map(row => parseMinutes(row.TaskCompletionTime))
    .filter(value => value != null)
    .reduce((maximum, value) => Math.max(maximum, value), 0);
  const jobs = orderedRows.map(toJobRow);
  return {
    techCode,
    techName: techNameOf(orderedRows[0]) || fallbackName,
    totalKm,
    overtimeMinutes: Math.max(0, lastCompletion - workEnd),
    etaViolationCount: jobs.filter(job => job.isCountedEtaViolation).length,
    servicePaperCount: jobs
      .filter(job => !job.isPlaceholder)
      .reduce((sum, job) => sum + job.servicePaperCount, 0),
    jobs,
  };
};

const buildRouteMap = (rows, options = {}) => {
  const center = resolveCenter(rows, options);
  const techCodes = [...new Set(rows.map(techCodeOf).filter(value => textKey(value) !== ''))];
  return new Map(techCodes.map(techCode => [
    textKey(techCode),
    summarizeRoute(techCode, rows, center),
  ]));
};

export const buildBoard = (calculatedRows, options = {}) => {
  const center = resolveCenter(calculatedRows, options);
  const routes = buildRouteMap(calculatedRows, options);
  return {
    serviceCenterLatitude: center.latitude,
    serviceCenterLongitude: center.longitude,
    technicians: [...routes.values()]
      .map(route => ({
        techCode: route.techCode,
        techName: route.techName,
        jobCount: route.jobs.filter(job => !job.isPlaceholder).length,
        servicePaperCount: route.servicePaperCount,
        totalKm: Number(route.totalKm.toFixed(1)),
        overtimeMinutes: route.overtimeMinutes,
        etaViolationCount: route.etaViolationCount,
        jobs: route.jobs,
      }))
      .filter(technician => technician.jobCount > 0)
      .sort((first, second) => first.techName.localeCompare(second.techName, 'tr')),
  };
};

const emptyRoute = (techCode, techName) => ({
  techCode,
  techName,
  totalKm: 0,
  overtimeMinutes: 0,
  etaViolationCount: 0,
  servicePaperCount: 0,
  jobs: [],
});

export const SKILL_SCORE_GROUPS = [
  { score: 100, label: 'Çalışır', description: 'bu teknisyen çalışır' },
  { score: 68, label: 'Gerekirse Çalışır', description: 'bu teknisyen gerekirse çalışır' },
  { score: 2, label: 'Çalışmaz', description: 'bu teknisyen çalışmaz' },
  { score: 21, label: 'Kesinlikle Çalışmaz', description: 'bu teknisyen kesinlikle çalışmaz' },
];

const skillScoreRank = score => {
  const index = SKILL_SCORE_GROUPS.findIndex(group => group.score === Number(score));
  return index === -1 ? SKILL_SCORE_GROUPS.length : index;
};

export const buildTechnicianSkillMap = (rows = []) => {
  const result = new Map();
  rows.forEach((row) => {
    const technicianId = textKey(read(row, 'TechnicianId', 'technicianId'));
    if (!technicianId) return;
    const score = Number(read(row, 'Score', 'score'));
    const skills = String(read(row, 'Skills', 'skills') || '')
      .split(',')
      .map(textKey)
      .filter(Boolean);
    if (!result.has(technicianId)) result.set(technicianId, { scoreBySkill: new Map() });
    const scoreBySkill = result.get(technicianId).scoreBySkill;
    skills.forEach((skillId) => {
      const currentScore = scoreBySkill.get(skillId);
      if (currentScore == null || skillScoreRank(score) < skillScoreRank(currentScore)) {
        scoreBySkill.set(skillId, score);
      }
    });
  });
  return result;
};

const technicianSkillScore = (skillMap, technicianRow, skillId) => {
  const requiredSkill = textKey(skillId);
  if (!requiredSkill) return null;
  const technicianIds = [techCodeOf(technicianRow), userTechCodeOf(technicianRow)]
    .map(textKey)
    .filter(Boolean);
  const scores = technicianIds
    .map(technicianId => skillMap.get(technicianId)?.scoreBySkill.get(requiredSkill))
    .filter(score => score != null)
    .sort((first, second) => skillScoreRank(first) - skillScoreRank(second));
  return scores[0] ?? null;
};

const routeDelta = (before, after) => ({
  techCode: before.techCode,
  techName: before.techName,
  jobCountBefore: before.jobs.filter(job => !job.isPlaceholder).length,
  servicePaperCountBefore: before.servicePaperCount,
  servicePaperCountAfter: after.servicePaperCount,
  kmBefore: Number(before.totalKm.toFixed(1)),
  kmAfter: Number(after.totalKm.toFixed(1)),
  kmDelta: Number((after.totalKm - before.totalKm).toFixed(1)),
  overtimeMinutesBefore: before.overtimeMinutes,
  overtimeMinutesAfter: after.overtimeMinutes,
  overtimeMinutesDelta: after.overtimeMinutes - before.overtimeMinutes,
  etaViolationCountBefore: before.etaViolationCount,
  etaViolationCountAfter: after.etaViolationCount,
  etaViolationCountDelta: after.etaViolationCount - before.etaViolationCount,
  routeAfter: after.jobs,
});

const moveRawRow = (row, ofscNr, targetReference) => {
  if (textKey(ofscNrOf(row)) !== textKey(ofscNr)) return row;
  const targetTechCode = techCodeOf(targetReference);
  const targetUserCode = read(targetReference, 'UserTechCode', 'KUL_TEK_KODU', 'userTechCode');
  const targetTechName = techNameOf(targetReference);
  return {
    ...row,
    TechCode: targetTechCode,
    TEKNISYEN_KODU: targetTechCode,
    UserTechCode: targetUserCode,
    KUL_TEK_KODU: targetUserCode,
    TechName: targetTechName,
    TEKNISYEN_ADI: targetTechName,
    TechWorkStart: read(targetReference, 'TechWorkStart', 'TECH_WORK_START', 'techWorkStart'),
    TechWorkEnd: read(targetReference, 'TechWorkEnd', 'TECH_WORK_END', 'techWorkEnd'),
  };
};

export const getMoveCandidates = (
  ofscNr,
  rawRows,
  calculatedRows,
  options = {},
  technicianSkillMap = new Map(),
  targetTechCodeFilter = null,
) => {
  const sourceRow = rawRows.find(row => textKey(ofscNrOf(row)) === textKey(ofscNr));
  if (!sourceRow) return null;

  const donorTechCode = techCodeOf(sourceRow);
  const simulationCenter = resolveCenter(calculatedRows, options);
  const baseline = buildRouteMap(calculatedRows, options);
  const donorBefore = baseline.get(textKey(donorTechCode));
  if (!donorBefore) return null;
  const job = donorBefore.jobs.find(item => textKey(item.ofscNr) === textKey(ofscNr));
  if (!job) return null;
  const requiredSkillId = skillIdOf(sourceRow);
  const donorSkillScore = technicianSkillScore(technicianSkillMap, sourceRow, requiredSkillId);

  const bucketTechCodes = new Set(rawRows
    .filter(isBucketNeighbourhood)
    .map(row => textKey(techCodeOf(row))));
  const targetReferences = [...new Map(rawRows
    .filter(row => (
      textKey(techCodeOf(row)) !== textKey(donorTechCode)
      && !bucketTechCodes.has(textKey(techCodeOf(row)))
      && (targetTechCodeFilter == null
        || textKey(techCodeOf(row)) === textKey(targetTechCodeFilter))
    ))
    .map(row => [textKey(techCodeOf(row)), row])).values()];
  const candidates = targetReferences.map((targetReference) => {
    const targetTechCode = techCodeOf(targetReference);
    const skillScore = technicianSkillScore(technicianSkillMap, targetReference, requiredSkillId);
    const targetBefore = baseline.get(textKey(targetTechCode));
    if (!targetBefore) return null;
    const movedRows = rawRows.map(row => moveRawRow(row, ofscNr, targetReference));
    const calculatedAfter = calculateToaScheduling(movedRows, {
      ...options,
      technicianFilter: [donorTechCode, targetTechCode],
    });
    const afterRoutes = buildRouteMap(calculatedAfter, {
      ...options,
      serviceCenter: simulationCenter,
    });
    const donorAfter = afterRoutes.get(textKey(donorTechCode))
      || emptyRoute(donorTechCode, donorBefore.techName);
    const targetAfter = afterRoutes.get(textKey(targetTechCode))
      || emptyRoute(targetTechCode, targetBefore.techName);
    const donor = routeDelta(donorBefore, donorAfter);
    const target = routeDelta(targetBefore, targetAfter);
    const fit = donor.etaViolationCountDelta > 0 || target.etaViolationCountDelta > 0
      ? 'EtaViolation'
      : donor.overtimeMinutesDelta > 0 || target.overtimeMinutesDelta > 0
        ? 'Overtime'
        : 'Fit';
    return {
      targetTechCode,
      targetTechName: targetBefore.techName,
      skillScore,
      fit,
      combinedKmDelta: Number((donor.kmDelta + target.kmDelta).toFixed(1)),
      donor,
      target,
    };
  }).filter(Boolean);
  const fitRank = { Fit: 0, Overtime: 1, EtaViolation: 2 };
  candidates.sort((first, second) => (
    skillScoreRank(first.skillScore) - skillScoreRank(second.skillScore)
    || fitRank[first.fit] - fitRank[second.fit]
    || first.combinedKmDelta - second.combinedKmDelta
  ));
  return {
    ofscNr,
    job,
    donorTechCode,
    donorTechName: donorBefore.techName,
    requiredSkillId,
    donorSkillScore,
    candidates,
  };
};

export const getMoveImpact = (
  ofscNr,
  targetTechCode,
  rawRows,
  calculatedRows,
  options = {},
) => {
  const result = getMoveCandidates(
    ofscNr,
    rawRows,
    calculatedRows,
    options,
    new Map(),
    targetTechCode,
  );
  const candidate = result?.candidates?.[0];
  if (!candidate) return null;
  return {
    ...candidate,
    ofscNr: result.ofscNr,
    donorTechCode: result.donorTechCode,
    donorTechName: result.donorTechName,
  };
};

export const getAllBestMoves = (
  rawRows,
  calculatedRows,
  options = {},
  technicianSkillMap = new Map(),
) => {
  const bestMoves = [];
  calculatedRows.filter(row => !isPlaceholder(row) && isMovable(row)).forEach((row) => {
    const bestMove = getBestMoveForJob(
      ofscNrOf(row), rawRows, calculatedRows, options, technicianSkillMap,
    );
    if (bestMove) bestMoves.push(bestMove);
  });
  return bestMoves;
};

export const getMovableJobIds = (calculatedRows = [], technicianCode = null) => calculatedRows
  .filter(row => (
    !isPlaceholder(row)
    && isMovable(row)
    && (technicianCode == null || textKey(techCodeOf(row)) === textKey(technicianCode))
  ))
  .map(ofscNrOf);

export const getBestMoveForJob = (
  ofscNr,
  rawRows,
  calculatedRows,
  options = {},
  technicianSkillMap = new Map(),
) => {
  const result = getMoveCandidates(
    ofscNr, rawRows, calculatedRows, options, technicianSkillMap,
  );
  const best = result?.candidates?.[0];
  if (!best) return null;
  return {
    ofscNr,
    bestTechCode: best.targetTechCode,
    bestTechName: best.targetTechName,
    skillScore: best.skillScore,
    fit: best.fit,
    combinedKmDelta: best.combinedKmDelta,
  };
};
